import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { searchProducts } from '../services/apiService';
import { getArticleImageUrl } from '../utils/imageUtils';
import PriceDisplay from './PriceDisplay';

const ProductAutocompleteInput = ({
  value,
  onChange,
  onSelectProduct,
  placeholder = 'Search products by pharmacode, name, or brand...',
  onShowMoreResults // NEW PROP
}) => {
  const [searchTerm, setSearchTerm] = useState(value ? String(value) : '');
  const [previewResults, setPreviewResults] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProductSelected, setIsProductSelected] = useState(false);
  const searchRef = useRef(null);
  const justSelected = useRef(false); // Add this ref

  useEffect(() => {
    setSearchTerm(value ? String(value) : '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowPreview(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }
    // Don't search if a product is selected
    if (typeof searchTerm === 'string' && searchTerm.trim().length >= 2 && !isProductSelected) {
      setIsLoading(true);
      setShowPreview(true);
      const fetchResults = async () => {
        try {
          const response = await searchProducts(searchTerm, 1, 5);
          if (response?.success && Array.isArray(response.data.products)) {
            setPreviewResults(response.data.products);
          } else {
            setPreviewResults([]);
          }
        } catch (error) {
          setPreviewResults([]);
        } finally {
          setIsLoading(false);
        }
      };
      const timer = setTimeout(fetchResults, 300);
      return () => clearTimeout(timer);
    } else {
      setPreviewResults([]);
      setShowPreview(false);
    }
  }, [searchTerm, isProductSelected]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    onChange?.(e.target.value);
    // Reset product selected state when user starts typing
    if (isProductSelected) {
      setIsProductSelected(false);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setPreviewResults([]);
    setShowPreview(false);
    setIsProductSelected(false);
    onChange?.('');
  };

  const handlePreviewClick = (product) => {
    const pharmacode = product.articles?.[0]?.pharmacode || product.pharmacode || product.productNumber;
    if (pharmacode) {
      setSearchTerm(pharmacode);
      onChange?.(pharmacode);
      onSelectProduct?.(product);
      setShowPreview(false);
      setPreviewResults([]); // Ensure dropdown closes and results are cleared
      setIsProductSelected(true); // Mark that a product has been selected
      justSelected.current = true; // Set flag
    }
  };

  const getOptimizedImageUrl = (images) => {
    if (!Array.isArray(images) || images.length === 0) return null;
    const picFront3D = images.find(image => image.includes('PICFRONT3D'));
    if (picFront3D) return picFront3D;
    const picFront = images.find(image => image.includes('PICFRONT'));
    if (picFront) return picFront;
    return images[0];
  };

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FaSearch className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder={placeholder}
          value={typeof searchTerm === 'string' ? searchTerm : ''}
          onChange={handleInputChange}
          className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {showPreview && typeof searchTerm === 'string' && searchTerm.trim().length >= 2 && !isProductSelected && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto"
          >
            {isLoading ? (
              <div className="p-6 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 border-r-transparent"></div>
                <p className="mt-2 text-sm text-gray-500">Searching...</p>
              </div>
            ) : previewResults.length > 0 ? (
              <motion.div 
                className="py-2 divide-y divide-gray-50"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.05
                    }
                  }
                }}
              >
                {previewResults.map((product) => {
                  const firstArticle = product.articles?.[0] || {};
                  const article = {
                    pharmacode: firstArticle.pharmacode || product.pharmacode || product.productNumber,
                    description: {
                      description: firstArticle.description?.description || product.description?.description || 'No description available',
                      longDescription: firstArticle.description?.longDescription || product.description?.longDescription || product.longDescription || firstArticle.description?.description || 'No description available'
                    },
                    hasPhoto: firstArticle.hasPhoto ?? product.hasPhoto ?? false,
                    images: Array.isArray(firstArticle.images)
                      ? { F: firstArticle.images, M: firstArticle.images }
                      : (firstArticle.images || (Array.isArray(product.images) ? { F: product.images } : (product.images || {}))),
                    price: firstArticle.price ?? null
                  };
                  return (
                    <motion.div
                      key={product._id}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: { opacity: 1, y: 0 }
                      }}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => handlePreviewClick(product)}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center space-x-4 transition-colors duration-200"
                    >
                      <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                        {(() => {
                          const imgUrl = getArticleImageUrl(article, 'F');
                          if (imgUrl) {
                            return (
                              <img
                                src={imgUrl}
                                alt={article.description.description}
                                className="w-full h-full object-contain"
                              />
                            );
                          }
                          return (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">
                          {article.description.longDescription}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Pharmacode: {article.pharmacode}
                        </p>
                        <PriceDisplay article={article} className="mt-1 text-indigo-600" />
                      </div>
                    </motion.div>
                  );
                })}
                {/* Show more results button if exactly 5 results */}
                {previewResults.length === 5 && (
                  <div className="px-4 py-2 bg-gray-50 text-center">
                    <button
                      type="button"
                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      onClick={() => {
                        setShowPreview(false);
                        onShowMoreResults?.(searchTerm);
                      }}
                    >
                      Show more results
                    </button>
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="mt-2 text-sm">No products found for "{typeof searchTerm === 'string' ? searchTerm : ''}"</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductAutocompleteInput; 